// updates the existing wrapper component to work with v6 react router
// essentially a copy paste of OptinProtectedRoute
// the only real change is that that wrapped component does not include another <Route/>
import React, { useEffect, useMemo, useRef } from 'react';
import { useStoreActions, useStoreState } from '../../store';
import { FeatureNotEnabled } from '../../pages/FeatureNotEnabled';
import { PageLoadingError } from '../../pages/PageLoadingError';
import { Redirect } from 'react-router-dom';
import { hasSufficientPermissions } from '../utils';
import { Skeleton } from 'antd';
import type { LayoutType } from '../LayoutWrapper';
import { LayoutWrapper } from '../LayoutWrapper';
import { useLocation, CompatRoute } from 'react-router-dom-v5-compat';
import type { UserPermissionType } from '../../store/services/AuthService/types';
import { useStoreSync } from '../../hooks/useStoreSync';
import { useSession } from '~/hooks/useSession';

export interface V6OptInProtectedRouteProps {
  requiredOptIn: string[];
  excludedOptIn?: string[];
  requiredFeatureMap?: Record<string, any>;
  path: string;
  requiredPermissionType: UserPermissionType;
  layoutType?: LayoutType;
  children: React.ReactNode;
  flexibleWidth?: boolean;
}

type Denial =
  | { kind: 'feature-disabled'; optIns: string[] }
  | { kind: 'feature-missing'; optIns: string[] }
  | { kind: 'opt-ins-unavailable' }
  | { kind: 'opt-in-missing'; optIns: string[] }
  | { kind: 'opt-in-excluded'; optIns: string[] }
  | { kind: 'insufficient-permissions' };

export const V6OptinProtectedRoute = ({
  requiredPermissionType,
  requiredOptIn = [],
  excludedOptIn = [],
  requiredFeatureMap,
  path,
  layoutType = 'BaseLayout',
  flexibleWidth = false,
  children,
}: V6OptInProtectedRouteProps) => {
  const user = useStoreState((state) => state.session.user);
  const throwAppError = useStoreActions((actions) => actions.errors.throwApplicationError);
  const location = useLocation();

  // Initialize TanStack Query and sync with Easy Peasy store
  const { partnerQuery } = useStoreSync();
  const { isOptInEnabled, isShipiumView, partnerOptInsStatus, refetchPartnerOptIns } = useSession();

  // Use TanStack Query data directly
  const activePartner = partnerQuery.data;

  const matches = requiredOptIn.filter((req) => isOptInEnabled(req));
  const excludedMatches = excludedOptIn.filter((req) => isOptInEnabled(req));

  // Only routes that actually consult the opt-ins should wait for them. Folding the
  // opt-ins wait into every route would make the 26 call sites that pass no opt-in rules
  // depend on a query they never read.
  const needsOptIns = requiredOptIn.length > 0 || excludedOptIn.length > 0;

  // The partner record and the partner opt-ins are two independent requests with
  // independent lifecycles, so either can still be in flight while the other has settled.
  // Access cannot be judged until the ones this route relies on are in hand: an opt-in
  // map that has not arrived yet is an empty map, and reading that as "not granted"
  // denies a user who is in fact entitled. That is CUS-18977 -- an intermittent,
  // latency-dependent 403 on routes the user has every right to open.
  //
  // Waiting is only correct while an answer is still coming. When the opt-ins are
  // `unavailable` -- the request failed, or it was never enabled -- no answer is coming,
  // so waiting would hang the route forever; that case is handled as its own denial below.
  const loading = partnerQuery.isLoading || (needsOptIns && partnerOptInsStatus === 'pending');

  const featureMap = useMemo(() => {
    return Object.entries(activePartner?.featureToggles || {})?.reduce((prev: Record<string, boolean>, e) => {
      const [key, val] = e;
      if (val === 'true') {
        prev[key] = true;
      } else if (val === 'false') {
        prev[key] = false;
      }
      return prev;
    }, {});
  }, [activePartner?.featureToggles]);

  const missingFeature = useMemo(() => {
    return Object.keys(requiredFeatureMap || {}).find((val: any) => {
      return (
        requiredFeatureMap &&
        ((featureMap.hasOwnProperty(val) && featureMap[val] !== requiredFeatureMap[val]) ||
          (!featureMap.hasOwnProperty(val) && requiredFeatureMap[val]))
      );
    });
  }, [requiredFeatureMap, featureMap]);

  // Pure: works out whether access is denied, without emitting anything.
  const resolveDenial = (): Denial | null => {
    if (loading) {
      return null;
    }
    if (!isShipiumView) {
      if (activePartner && requiredFeatureMap && !!missingFeature) {
        return requiredFeatureMap[missingFeature]
          ? { kind: 'feature-missing', optIns: [missingFeature] }
          : { kind: 'feature-disabled', optIns: [missingFeature] };
      }
      // Never let "we could not find out" collapse into "you are not entitled" -- that
      // conflation is the defect this route guard exists to stop making.
      if (needsOptIns && partnerOptInsStatus === 'unavailable') {
        return { kind: 'opt-ins-unavailable' };
      }
      if (requiredOptIn.length > 0 && matches.length === 0) {
        return { kind: 'opt-in-missing', optIns: requiredOptIn };
      }
      if (excludedOptIn.length > 0 && excludedMatches.length > 0) {
        return { kind: 'opt-in-excluded', optIns: excludedOptIn };
      }
    }
    if (user && !hasSufficientPermissions(user, requiredPermissionType)) {
      return { kind: 'insufficient-permissions' };
    }
    return null;
  };

  const denial = resolveDenial();

  // Raising the notification from an effect rather than from render keeps a transient
  // state from leaving a toast on screen after the page has already recovered, and keeps
  // the store mutation out of React's render phase.
  //
  // The key uses the route pattern, not the resolved pathname: a denied user paging
  // through `shipment/1`, `shipment/2`, `shipment/3` is one denial, not three. The
  // message still names the page the user is actually looking at, read through a ref so
  // it stays current without re-arming the effect on every navigation within the route.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  const denialKey = denial ? `${denial.kind}:${path}` : null;
  useEffect(() => {
    if (!denialKey) {
      return;
    }
    throwAppError({
      error: new Error(`Access to "${pathnameRef.current}" denied`),
      message: `Access to "${pathnameRef.current}" denied`,
      asNotification: true,
    });
  }, [denialKey, throwAppError]);

  const renderContent = () => {
    if (loading) {
      return <Skeleton />;
    }
    switch (denial?.kind) {
      case 'feature-disabled':
        return <FeatureNotEnabled disabledFeature requestedOptIn={denial.optIns} />;
      case 'feature-missing':
        return <FeatureNotEnabled requestedOptIn={denial.optIns} />;
      case 'opt-ins-unavailable':
        return <PageLoadingError onRetry={refetchPartnerOptIns} />;
      case 'opt-in-missing':
        return <FeatureNotEnabled requestedOptIn={denial.optIns} />;
      case 'opt-in-excluded':
        return <FeatureNotEnabled disabledFeature requestedOptIn={denial.optIns} />;
      case 'insufficient-permissions':
        return <Redirect to="/access-denied" />;
      default:
        return children;
    }
  };

  return (
    <CompatRoute
      path={path}
      render={() => (
        <LayoutWrapper layoutType={layoutType} flexibleWidth={flexibleWidth}>
          {renderContent()}
        </LayoutWrapper>
      )}
    />
  );
};
